import { z } from "zod"

import i18n from "@/i18n/config"
import { calculateAgeFromBirthDate } from "@/utils/ageUtils"

const t = i18n.t

export type RefinementFn<T> = (
  data: T,
  ctx: z.RefinementCtx
) => void | Promise<void>

export const createBasePassHolderSchema = () =>
  z.object({
    firstName: z
      .string()
      .min(1, t("passHolderDetails.form.errors.firstNameRequired")),
    lastName: z
      .string()
      .min(1, t("passHolderDetails.form.errors.lastNameRequired")),
    birthDate: z
      .string()
      .regex(
        /^\d{6}$|^\d{8}$/,
        t("passHolderDetails.form.errors.birthDateFormat", {
          format: t("passHolderDetails.form.birthDateFormat"),
        })
      )
      .transform((val) => (val.length === 8 ? val.slice(2) : val)),
    passSelection: z.enum(["new", "existing"]),
    insurance: z.boolean().optional(),
    wtpNumber: z.string().optional(),
    passHolderImage: z
      .object({
        id: z.string(),
        base64: z.string(),
        fileName: z.string(),
      })
      .optional(),
    personCertificate: z
      .object({
        id: z.string(),
        fileName: z.string(),
      })
      .optional(),
    exitingPhotoId: z.string().nullable().optional(),
  })

export type PassHolderFormData = z.infer<
  ReturnType<typeof createBasePassHolderSchema>
>

export const createPersonDataSchema = () =>
  createBasePassHolderSchema().pick({
    firstName: true,
    lastName: true,
    birthDate: true,
  })

export const createPassHolderSchema = (
  requireImage: boolean,
  requireCertificate: boolean,
  minAge: number,
  maxAge: number,
  startDate?: Date,
  refinements: RefinementFn<PassHolderFormData>[] = [],
  requireInsurance: boolean = true
) => {
  let schema = createBasePassHolderSchema()

  if (requireInsurance) {
    schema = schema.refine((data) => data.insurance !== undefined, {
      message: t("passHolderDetails.form.errors.insuranceRequired"),
      path: ["insurance"],
    })
  }

  if (minAge !== undefined && maxAge !== undefined) {
    schema = schema.superRefine((data, ctx) => {
      const age = calculateAgeFromBirthDate(data.birthDate, startDate)

      if (age === null) {
        ctx.addIssue({
          code: "custom",
          path: ["birthDate"],
          message: t("passHolderDetails.form.errors.birthDateInvalid", {
            format: t("passHolderDetails.form.birthDateFormat"),
          }),
        })
        return
      }

      if (age === null || age < minAge || age > maxAge) {
        ctx.addIssue({
          code: "custom",
          path: ["birthDate"],
          message: t("passHolderDetails.form.errors.birthDateAgeSpan", {
            minAge,
            maxAge,
          }),
        })
      }
    })
  }

  if (requireImage) {
    schema = schema.refine(
      (data) => {
        const hasId =
          data.passHolderImage?.id && data.passHolderImage.id.length > 0
        const hasExitingPhotoId =
          data.exitingPhotoId !== null && data.exitingPhotoId !== undefined

        return hasId || hasExitingPhotoId
      },
      {
        message: t("passHolderDetails.form.errors.imageRequired"),
        path: ["passHolderImage"],
      }
    )
  }

  if (requireCertificate) {
    schema = schema.refine(
      (data) =>
        data.personCertificate?.id && data.personCertificate.id.length > 0,
      {
        message: t("passHolderDetails.form.errors.personCertificateRequired"),
        path: ["personCertificate"],
      }
    )
  }

  refinements.forEach((refineFn) => {
    schema = schema.superRefine(refineFn)
  })

  return schema
}

export const createMemberCouponSchema = () =>
  z.object({
    couponCode: z
      .string()
      .min(
        4,
        t("passHolderDetails.form.errors.memberCouponTooShort", {
          minLength: 4,
        })
      )
      .transform((val) => val.toUpperCase().trim()),
  })

export type MemberCouponFormData = z.infer<
  ReturnType<typeof createMemberCouponSchema>
>
